import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Spinner, Stack, Table } from 'react-bootstrap'
import { DateRange, DayPicker } from 'react-day-picker'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import { format } from 'date-fns/format'
import { ru } from 'date-fns/locale'
import Modal from 'react-bootstrap/Modal'

import Badge from '../../components/_atoms/Badge/Badge'
import BadgeStatus from '../../components/_atoms/BadgeStatus/BadgeStatus'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import ColumnSortRTK from '../../components/_molecules/ColumnSort/ColumnSortRTK'
import ModalDataVendorDocument from '../../components/_molecules/ModalDataVendorDocument/ModalDataVendorDocument'
import SearchComponentRTK from '../../components/_molecules/SearchComponent/SearchComponentRTK'
import { fontSizes, fontWeights } from '../../constants/salesStyles'
import { ToastType, useToast } from '../../hooks/useToast'
import { useSelector } from '../../modules/store/customSelector'
import {
    useExportDealsListMutation,
    useGetDealsListMutation,
    useGetDealStagesInfoQuery,
    useGetDealStatusDictQuery,
} from '../../modules/store/dochubApi/dochubApi'
import { DealStage } from '../../modules/store/dochubApi/dochubTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { SortFieldDeals, SortIconType, SortOrder } from '../../types/common/commonTypes'
import { Marketplaces, MarketplacesKeys, PaySources, PaySourcesKeys } from '../../types/part/partTypes'
import { formatDate } from '../../utils/converters'
import { formatPrice } from '../../utils/formatAndUnformatPrice'
import { getCurrencySymbol } from '../../utils/getCurrencySymbol'
import { getEndOfDay } from '../../utils/getEndOfDay'
import { parseSortOrder } from '../../utils/parseSortOrder'
import { colWidth } from '../../utils/stylePatches'

import styles from './styles.module.scss'
import TableHeadSelectPaySource from './TableHeadSelectPaySource/TableHeadSelectPaySource'
import { getMetaPaySources } from './utils/getMetaPaySources'

type DealsPageFilters = {
    page: number
    perPage: number
    activeStage: DealStage
    sortOrder: [string, SortOrder][]
    paySource: string | null
    dateFrom: number | null
    dateTo: number | null
    search: string
}

const DealList = () => {
    const navigate = useNavigate()

    const {
        data: dealStatusDictData,
        isLoading: isLoadingGetDealStatusDict,
        isError: isErrorGetDealStatusDict,
    } = useGetDealStatusDictQuery()
    const {
        data: dealStagesInfo,
        isLoading: isLoadingGetDealStagesInfo,
        isError: isErrorGetDealStagesInfo,
    } = useGetDealStagesInfoQuery({})
    const [getDealsList, { data: dealsList, isLoading: isLoadingGetDealsList, isError: isErrorGetDealsList }] =
        useGetDealsListMutation()
    const [exportDealsList, { isLoading: isExportingDealsList, isError: isErrorExportDealsList }] =
        useExportDealsListMutation()
    const [dealsCount, setDealsCount] = useState(0)
    const currentCurrency = useSelector(state => state.partList.currentCurrency)

    const [searchParams] = useSearchParams()
    const searchActiveStage = (searchParams.get('activeStage') as DealStage) || DealStage.inprogress
    const searchPage = Number(searchParams.get('page')) || 1
    const searchSortOrder = searchParams.get('sortOrder')
    const searchPaySource = searchParams.get('paySource')
    const searchDateFrom = searchParams.get('dateFrom')
    const searchDateTo = searchParams.get('dateTo')
    const searchSearch = searchParams.get('search') || ''

    const parsedSortOrder = useMemo(() => parseSortOrder(searchSortOrder), [searchSortOrder])

    const [filters, setFilters] = useState<DealsPageFilters>({
        page: searchPage,
        perPage: 50,
        activeStage: searchActiveStage,
        sortOrder: parsedSortOrder,
        paySource: searchPaySource,
        dateFrom: Number(searchDateFrom) || null,
        dateTo: Number(searchDateTo) || null,
        search: searchSearch,
    })
    const { page, perPage, activeStage, sortOrder, paySource, dateFrom, dateTo, search } = filters

    // Вычисляем текущую сортировку для поля "Дата изменения"
    const currentSortOrderForDate = sortOrder.find(item => item[0] === SortFieldDeals.CREATED_AT)?.[1] || SortOrder.NONE

    const [isModalDataVendorOpen, setIsModalDataVendorOpen] = useState(false)

    const statistics = dealsList?.totalSum &&
        dealsList?.totalQty && {
            totalSum: dealsList.totalSum,
            totalQty: dealsList.totalQty,
            averageCheck: dealsList.totalSum / dealsList.totalQty,
        }

    // Состояния для календаря
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>(undefined)

    const openDeal = useCallback(
        (dealId: string) => {
            navigate(getAdminNavigationPath(adminRouteAlias.deals.edit) + `/${dealId}`)
        },
        [navigate],
    )

    const setFiltersAndUpdateUrl = (newFilters: DealsPageFilters) => {
        // Обновляем состояние фильтров
        setFilters(newFilters)

        // Обновляем URL с новыми параметрами
        const newParams = new URLSearchParams()

        // Сохраняем параметры фильтров
        if (newFilters.page > 1) {
            newParams.set('page', String(newFilters.page))
        }
        if (newFilters.activeStage) {
            newParams.set('activeStage', newFilters.activeStage)
        }
        if (newFilters.sortOrder.length) {
            newParams.set(
                'sortOrder',
                newFilters.sortOrder.length ? newFilters.sortOrder.map(pair => pair.join(',')).join(';') : '',
            )
        }
        if (newFilters.paySource) {
            newParams.set('paySource', newFilters.paySource)
        }
        if (newFilters.dateFrom) {
            newParams.set('dateFrom', String(newFilters.dateFrom))
        }
        if (newFilters.dateTo) {
            newParams.set('dateTo', String(newFilters.dateTo))
        }
        if (newFilters.search) {
            newParams.set('search', newFilters.search)
        }

        // Обновляем URL без перезаписи истории
        navigate(
            {
                pathname: getAdminNavigationPath(adminRouteAlias.deals.list),
                search: newParams.toString(), // Все параметры фильтров
            },
            { replace: false },
        ) // Это сохраняет текущий URL в истории
    }

    const setPage = (newPage: number) => {
        setFiltersAndUpdateUrl({
            ...filters,
            page: newPage,
        })
    }

    const setSortOrder = (field: string, newOrder: SortOrder) => {
        const newSort = sortOrder.filter(item => item[0] !== field)
        if (newOrder !== SortOrder.NONE) {
            newSort.push([field, newOrder])
        }
        setFiltersAndUpdateUrl({
            ...filters,
            sortOrder: newSort,
            page: 1,
        })
    }

    const setPaySource = (newPaySource: string | null) => {
        setFiltersAndUpdateUrl({
            ...filters,
            paySource: newPaySource,
            page: 1,
        })
    }

    const setDateRange = (newDateRange: DateRange | undefined) => {
        setFiltersAndUpdateUrl({
            ...filters,
            dateFrom: newDateRange?.from?.getTime() || null,
            dateTo: getEndOfDay(newDateRange?.to)?.getTime() || null,
            page: 1,
        })
    }

    const exportParams = useMemo(() => {
        if (!dealStagesInfo) return null

        return {
            status: dealStagesInfo.data[activeStage].statuses,
            order: parsedSortOrder,
            ...(paySource && { paySource }),
            dateRange: {
                ...(dateFrom && { from: dateFrom }),
                ...(dateTo && { to: dateTo }),
            },
            ...(search && { search }),
        }
    }, [activeStage, dealStagesInfo, parsedSortOrder, paySource, dateFrom, dateTo, search])

    const handleExportDeals = useCallback(async () => {
        if (!exportParams) return

        try {
            await exportDealsList({ params: exportParams }).unwrap()
        } catch {
            // ошибка обрабатывается через useToast
        }
    }, [exportDealsList, exportParams])

    const renderListPagination = useCallback(
        (children?: ReactNode, showExportButton = false) => {
            if (!isLoadingGetDealsList && dealsCount === 0) return null

            return (
                <div className={styles.statisticsBlock}>
                    {children}
                    {showExportButton && (
                        <Button
                            variant="outline-primary"
                            disabled={isExportingDealsList || !exportParams}
                            onClick={handleExportDeals}
                        >
                            {isExportingDealsList && (
                                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                            )}
                            Скачать xlsx файл
                        </Button>
                    )}
                    <div className={styles.wrapRight}>
                        <ListPagination
                            page={page}
                            setPage={setPage}
                            perPage={perPage}
                            itemsCount={dealsCount}
                            justifyContent="flex-end"
                        />
                    </div>
                </div>
            )
        },
        [dealsCount, exportParams, handleExportDeals, isExportingDealsList, isLoadingGetDealsList, page, perPage],
    )

    const formatRange = (range?: DateRange) => {
        if (!range?.from) return 'Не выбрано'
        if (!range.to) return `с ${format(range.from, 'dd.MM.yyyy')}`
        return `${format(range.from, 'dd.MM.yyyy')} — ${format(range.to, 'dd.MM.yyyy')}`
    }

    const currentDateRange: DateRange | undefined = useMemo(() => {
        if (!dateFrom && !dateTo) return undefined
        return {
            from: dateFrom ? new Date(dateFrom) : undefined,
            to: dateTo ? new Date(dateTo) : undefined,
        }
    }, [dateFrom, dateTo])

    useEffect(() => {
        if (isLoadingGetDealStagesInfo) return
        if (!dealStagesInfo) return

        getDealsList({
            params: {
                pagination: { page, perPage },
                status: dealStagesInfo.data[activeStage].statuses,
                order: parsedSortOrder,
                ...(paySource && { paySource }),
                dateRange: {
                    ...(dateFrom && { from: dateFrom }),
                    ...(dateTo && { to: dateTo }),
                },
                ...(search && { search }),
            },
        })
    }, [
        activeStage,
        dealStagesInfo,
        getDealsList,
        isLoadingGetDealStagesInfo,
        page,
        paySource,
        perPage,
        parsedSortOrder,
        dateFrom,
        dateTo,
        search,
    ])

    useEffect(() => {
        dealsList && setDealsCount(dealsList.dataCount)
    }, [dealsList])

    // Обновление фильтров при изменении URL (например, при нажатии "Назад")
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            page: searchPage,
            activeStage: searchActiveStage,
            sortOrder: parsedSortOrder,
            paySource: searchPaySource,
            dateFrom: Number(searchDateFrom) || null,
            dateTo: Number(searchDateTo) || null,
            search: searchSearch,
        }))
    }, [parsedSortOrder, searchActiveStage, searchDateFrom, searchDateTo, searchPage, searchPaySource, searchSearch])

    useToast({ isTriggered: isErrorGetDealsList, type: ToastType.ERROR, message: 'Ошибка загрузки сделок' })
    useToast({
        isTriggered: isErrorGetDealStagesInfo,
        type: ToastType.ERROR,
        message: 'Ошибка загрузки информации о стадиях сделок',
    })
    useToast({
        isTriggered: isErrorGetDealStatusDict,
        type: ToastType.ERROR,
        message: 'Ошибка загрузки словаря статусов',
    })
    useToast({
        isTriggered: isErrorExportDealsList,
        type: ToastType.ERROR,
        message: 'Ошибка экспорта сделок',
    })

    return (
        <div className={styles.wrap}>
            <ToastContainer position="bottom-center" autoClose={5000} />
            {isModalDataVendorOpen && (
                <ModalDataVendorDocument
                    isOpen={isModalDataVendorOpen}
                    closeModal={() => setIsModalDataVendorOpen(false)}
                />
            )}
            <div className={styles.rowWrap}>
                <SalesText
                    text={'Сделки'}
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
            </div>
            <div className={styles.searchWrap}>
                <SearchComponentRTK
                    searchTerm={filters.search}
                    setSearchTerm={(newTerm) =>
                        setFiltersAndUpdateUrl({
                            ...filters,
                            search: newTerm,
                            page: 1,
                        })
                    }
                    placeholder="Поиск по сделкам"
                />
            </div>
            <Stack direction="horizontal">
                {dealStagesInfo && (
                    <Stack direction="horizontal" gap={4}>
                        {Object.entries(dealStagesInfo.data).map(([key, stageInfo]) => (
                            <Button
                                key={key}
                                variant={activeStage === key ? 'secondary' : 'outline-secondary'}
                                style={{ width: '130px' }}
                                onClick={() =>
                                    setFiltersAndUpdateUrl({
                                        ...filters,
                                        activeStage: key as DealStage,
                                        page: 1,
                                    })
                                }
                            >
                                {stageInfo.title}
                            </Button>
                        ))}
                    </Stack>
                )}
                <div className="ms-auto">
                    <Button
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => navigate(getAdminNavigationPath(adminRouteAlias.analytics.deals))}
                    >
                        Аналитика по сделкам
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => navigate(getAdminNavigationPath(adminRouteAlias.deals.new))}
                    >
                        + Новая сделка
                    </Button>
                </div>
            </Stack>
            <Stack direction="horizontal">
                <Button
                    variant="secondary"
                    onClick={() => {
                        setTempDateRange(currentDateRange)
                        setIsDatePickerOpen(true)
                    }}
                >
                    Выберите даты
                </Button>
                <span style={{ marginLeft: '24px' }}>{formatRange(currentDateRange)}</span>
                <div className="ms-auto">
                    <Button variant="primary" onClick={() => setIsModalDataVendorOpen(true)}>
                        Данные для документов
                    </Button>
                </div>
            </Stack>
            {renderListPagination(
                <>
                    {statistics && (
                        <>
                            <p className={styles.statisticsBlockText}>
                                Выручка за период: <b>{statistics.totalSum.toLocaleString('ru-RU')}</b>{' '}
                                {getCurrencySymbol(currentCurrency)}
                            </p>
                            <p className={styles.statisticsBlockText}>
                                Продано запчастей: <b>{statistics.totalQty.toLocaleString('ru-RU')}</b>
                            </p>
                            <p className={styles.statisticsBlockText}>
                                Средний чек: <b>{statistics.averageCheck.toLocaleString('ru-RU')}</b>{' '}
                                {getCurrencySymbol(currentCurrency)}
                            </p>
                        </>
                    )}
                </>,
            )}
            <Table striped hover>
                <thead>
                    <tr>
                        <th scope="col" style={colWidth(100)}>
                            Номер
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Дата
                            <ColumnSortRTK
                                iconType={SortIconType.NUMERIC}
                                sortField={SortFieldDeals.CREATED_AT}
                                reverseSort={true}
                                currentSortOrder={currentSortOrderForDate}
                                onSortChange={setSortOrder}
                            />
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Покупатель
                        </th>
                        <th scope="col" style={colWidth(250)}>
                            Запчасти
                        </th>
                        <th scope="col" style={colWidth(70)} className={styles.priceCell}>
                            Сумма
                        </th>
                        <th scope="col" style={colWidth(70)} className={styles.priceCell}>
                            Кол.
                        </th>
                        <th scope="col" style={colWidth(150)}>
                            Статус
                        </th>
                        <th scope="col" style={colWidth(200)}>
                            Комментарий
                        </th>
                        <th scope="col" style={colWidth(150)}>
                            <TableHeadSelectPaySource selected={filters.paySource} onChange={setPaySource} />
                        </th>
                        <th scope="col" style={colWidth(100)}>
                            Ответственный
                        </th>
                    </tr>
                </thead>
                {isLoadingGetDealsList ? (
                    <ListSkeleton rowCount={9} columnCount={10} marginVerticalRem={0.21} />
                ) : (
                    dealsList && (
                        <tbody>
                            {dealsList.data.map((deal, index) => (
                                <tr key={index} className={styles.row} onClick={() => openDeal(deal.id)}>
                                    <td scope="row">{deal.code}</td>
                                    <td scope="row">
                                        <Badge text={formatDate(deal.createdAt)} color="#ddd" fontColor="#555" />
                                    </td>
                                    <td scope="row">{deal.client?.name}</td>
                                    <td scope="row">
                                        {deal.items
                                            ?.map(item => {
                                                const partTitle = item.title || item.part.customTitle || item.part.title
                                                const vendorCode = item.part.vendorCode
                                                return `${partTitle} ${vendorCode}`
                                            })
                                            .join(', ')}
                                    </td>
                                    <td scope="row" className={styles.priceCell}>
                                        {formatPrice(deal.itemSum || 0)}
                                    </td>
                                    <td scope="row" className={styles.priceCell}>
                                        <div>{deal.items?.reduce((acc, cur) => acc + cur.qty, 0)} шт</div>
                                    </td>
                                    <td scope="row">
                                        <BadgeStatus
                                            text={
                                                dealStatusDictData
                                                    ? dealStatusDictData.data[deal.status]?.name || '---'
                                                    : deal.status
                                            }
                                            status={deal.status}
                                        />
                                    </td>
                                    <td scope="row">{deal.meta?.comment}</td>
                                    <td scope="row">
                                        {deal.meta?.source && Marketplaces[deal.meta.source as MarketplacesKeys]}
                                        {getMetaPaySources(deal).map(source => (
                                            <div key={source}>
                                                <Badge
                                                    text={PaySources[source as PaySourcesKeys] || '---'}
                                                    color="#ddd"
                                                    fontColor="#555"
                                                />
                                            </div>
                                        ))}
                                    </td>
                                    <td scope="row">{deal.seller?.name}</td>
                                </tr>
                            ))}
                        </tbody>
                    )
                )}
            </Table>
            {!isLoadingGetDealsList && dealsCount === 0 ? <ListNoItems /> : renderListPagination(undefined, true)}

            {isDatePickerOpen && (
                <Modal
                    show={isDatePickerOpen}
                    onHide={() => setIsDatePickerOpen(false)}
                    centered
                    dialogClassName={styles.modal}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Выберите диапазон дат</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <DayPicker
                            animate
                            captionLayout="dropdown"
                            mode="range"
                            selected={tempDateRange}
                            onSelect={range => setTempDateRange(range || undefined)}
                            navLayout="around"
                            locale={ru}
                            defaultMonth={tempDateRange?.from}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setTempDateRange(undefined)}>
                            Сбросить
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setDateRange(tempDateRange)
                                setIsDatePickerOpen(false)
                            }}
                        >
                            Применить
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    )
}

export default DealList
